import { useEffect } from 'react';
import { useDarkMode } from './DarkModeContext';
import { isClerkAuth } from '../config/auth';

/**
 * Updates Clerk's global theme based on the site's dark mode state.
 * Only applies CSS variable overrides when AUTH_PROVIDER=clerk.
 */
export const ClerkThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isDarkMode } = useDarkMode();

  useEffect(() => {
    // Skip Clerk theming entirely when not using Clerk
    if (!isClerkAuth) return;

    const root = document.documentElement;

    // Inject custom CSS for Clerk components
    let styleElement = document.getElementById('clerk-theme-override');
    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = 'clerk-theme-override';
      document.head.appendChild(styleElement);
    }

    if (isDarkMode) {
      // Dark mode Clerk CSS variables
      root.style.setProperty('--clerk-color-primary', '#ADDD30');
      root.style.setProperty('--clerk-color-primary-foreground', '#000000');
      root.style.setProperty('--clerk-color-danger', '#EF4444');
      root.style.setProperty('--clerk-color-success', '#ADDD30');
      root.style.setProperty('--clerk-color-warning', '#F36B16');
      root.style.setProperty('--clerk-color-neutral', '#FFFFFF');
      root.style.setProperty('--clerk-color-foreground', '#FFFFFF');
      root.style.setProperty('--clerk-color-muted-foreground', '#B3B3B3');
      root.style.setProperty('--clerk-color-background', '#1A1A1A');
      root.style.setProperty('--clerk-color-input', '#26262B');
      root.style.setProperty('--clerk-color-input-foreground', '#FFFFFF');
      root.style.setProperty('--clerk-color-ring', '#ADDD30');
      root.style.setProperty('--clerk-color-shimmer', '#26262B');
      root.style.setProperty('--clerk-color-modal-backdrop', 'rgba(0, 0, 0, 0.8)');
      root.style.setProperty('--clerk-border-radius', '0.375rem');
      root.style.setProperty('--clerk-spacing', '1rem');

      styleElement.textContent = `
        .cl-socialButtonsBlockButton,
        .cl-socialButtonsIconButton {
          background-color: #2d2d2d !important;
        }
        .cl-socialButtonsBlockButton:hover,
        .cl-socialButtonsIconButton:hover {
          background-color: #3a3a3a !important;
        }
        .cl-formFieldInput:focus,
        .cl-input:focus,
        input[class*="cl-"]:focus {
          outline: none !important;
          border-color: #ADDD30 !important;
          box-shadow: 0 0 0 2px rgba(173, 221, 48, 0.2) !important;
        }
      `;
    } else {
      // Light mode Clerk CSS variables
      root.style.setProperty('--clerk-color-primary', '#ADDD30');
      root.style.setProperty('--clerk-color-primary-foreground', '#FFFFFF');
      root.style.setProperty('--clerk-color-danger', '#EF4444');
      root.style.setProperty('--clerk-color-success', '#ADDD30');
      root.style.setProperty('--clerk-color-warning', '#F36B16');
      root.style.setProperty('--clerk-color-neutral', '#000000');
      root.style.setProperty('--clerk-color-foreground', '#000000');
      root.style.setProperty('--clerk-color-muted-foreground', '#747686');
      root.style.setProperty('--clerk-color-background', '#FFFFFF');
      root.style.setProperty('--clerk-color-input', '#FFFFFF');
      root.style.setProperty('--clerk-color-input-foreground', '#000000');
      root.style.setProperty('--clerk-color-ring', '#ADDD30');
      root.style.setProperty('--clerk-color-shimmer', '#FFFFFF');
      root.style.setProperty('--clerk-color-modal-backdrop', '#000000');
      root.style.setProperty('--clerk-border-radius', '0.375rem');
      root.style.setProperty('--clerk-spacing', '1rem');

      styleElement.textContent = `
        .cl-formFieldInput:focus,
        .cl-input:focus,
        input[class*="cl-"]:focus {
          outline: none !important;
          border-color: #ADDD30 !important;
          box-shadow: 0 0 0 2px rgba(173, 221, 48, 0.2) !important;
        }
      `;
    }
  }, [isDarkMode]);

  return <>{children}</>;
};
